<template>
  <AppDialogContent
    v-model="dialogValue"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType" style="align-self: center">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else style="align-self: center">
          <div class="text-h5 font-weight-bold mb-2">
            {{
              $t(
                "events:entryAction.editAdditionalDetails.heading",
                "Edit additional details"
              )
            }}
          </div>
          <p>
            {{
              $t(
                "events:entryAction.editAdditionalDetails.explanation.text",
                "You are going to add or edit additional details for your entry:"
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            :disabled="formSubmitted"
            class="mx-0"
            variant="text"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="() => (dialogValue = false)"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #reason>
            <div v-if="responseType == 'error'">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="entryLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="entry">
        <AppShowEventDetails v-if="event" :event="event" show-logo />

        <br />

        <AppEntryRegistrationOverview v-bind="{ registration, competition }">
          <template #competitionCodeheader>
            {{
              $t(
                "events:entryAction.editAdditionalDetails.competitionCode.label",
                "Entry"
              )
            }}
          </template>
          <template #athleteNamesHeader>
            {{
              $t(
                "events:entryAction.editAdditionalDetails.athleteNames.label",
                "Athlete"
              )
            }}
          </template>
          <template #horseNamesHeader>
            {{
              $t(
                "events:entryAction.editAdditionalDetails.horseNames.label",
                "Horse"
              )
            }}
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <template v-if="initFormLoading">
          <v-progress-linear indeterminate />
          <br />
        </template>

        <AppSelectPerson
          v-if="v$.form.vaultingLungerPersonId"
          v-model="form.vaultingLungerPersonId"
          :input-props="{
            label: $t(
              'events:entryAction.editAdditionalDetails.lunger.label',
              'Lunger'
            ),
            disabled: fieldDisabled('vaultingLungerPersonId'),
            errorMessages: fieldErrorMessages('vaultingLungerPersonId')
          }"
          show-details
          data-test="vaultingLungerPersonId"
          @blur="() => v$.form.vaultingLungerPersonId.$touch()"
        />

        <AppSelectPerson
          v-if="v$.form.vaultingReserveLungerPersonId"
          v-model="form.vaultingReserveLungerPersonId"
          :input-props="{
            label: $t(
              'events:entryAction.editAdditionalDetails.reserveLunger.label',
              'Reserve lunger'
            ),
            disabled: fieldDisabled('vaultingReserveLungerPersonId'),
            errorMessages: fieldErrorMessages('vaultingReserveLungerPersonId')
          }"
          show-details
          data-test="vaultingReserveLungerPersonId"
          @blur="() => v$.form.vaultingReserveLungerPersonId.$touch()"
        />

        <AppSelectPerson
          v-if="v$.form.groomPersonId"
          v-model="form.groomPersonId"
          :input-props="{
            label: $t(
              'events:entryAction.editAdditionalDetails.groom.label',
              'Groom'
            ),
            disabled: fieldDisabled('groomPersonId'),
            errorMessages: fieldErrorMessages('groomPersonId')
          }"
          show-details
          data-test="groomPersonId"
          outlined
          @blur="() => v$.form.groomPersonId.$touch()"
        />
      </div>
    </template>

    <template #submit>
      {{
        $t(
          "events:entryAction.editAdditionalDetails.saveDetails.button",
          "Save details"
        )
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="() => (dialogValue = false)"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script setup>
import { ref, computed } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import { useFormValidation } from "@/composables/form/form_validation";
import {
  useEntryFeiActionsInjections,
  injectEntryFeiEntryEditFragment
} from "./compositions";
import { DisplayTextFragments } from "@composables/display_text";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";
import AppSelectPerson from "@AppSelects/AppSelectPerson.vue";

import EntryFeiResponse from "./EntryFeiResponse.vue";
import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";

import { injectSnackbarError } from "@/composables/injections";
import { useTranslation } from "i18next-vue";
import { useQuery } from "@vue/apollo-composable";

defineOptions({
  name: "EntryFeiEntryEdit"
});

const { fragments, refetchQueries } = useEntryFeiActionsInjections();
const { snackbarServerError } = injectSnackbarError();
const { t } = useTranslation();

const entryLoading = ref(false);
const entry = ref(null);
const mutationFragment = injectEntryFeiEntryEditFragment();

const modal = ref(false);
const formSubmitted = ref(false);
const responseType = ref(null);
const mutationErrors = ref([]);
const formError = ref({});
const initEntryUpdateDetailsForm = ref({
  vuelidateValidations: {},
  readOnlyFields: []
});

const form = ref({
  entryId: null,
  vaultingLungerPersonId: null,
  vaultingReserveLungerPersonId: null,
  groomPersonId: null
});

const {
  v$,
  fieldDisabled,
  fieldErrorMessages,
  formValidate,
  formErrorMessages
} = useFormValidation(initEntryUpdateDetailsForm, form);

const registration = computed(() => {
  return entry.value?.registration;
});
const event = computed(() => {
  return entry.value?.registration?.event;
});
const competition = computed(() => {
  return entry.value?.competition;
});
const dialogValue = computed({
  get() {
    return modal.value;
  },
  set(v) {
    if (v == true && !modal.value) {
      responseType.value = null;
      mutationErrors.value = [];
    }
    modal.value = v;
  }
});

const responseTexts = computed(() => {
  switch (responseType.value) {
    case "success":
      return {
        headline: t(
          "events:entryAction.editAdditionalDetails.success.heading",
          "Editing entry details"
        ),
        main: t(
          "events:entryAction.editAdditionalDetails.success.mainMessage.text",
          "All changes saved"
        ),
        okBtn: t(
          "events:entryAction.editAdditionalDetails.success.ok.button",
          "OK"
        )
      };
    case "error":
      return {
        headline: t(
          "events:entryAction.editAdditionalDetails.failed.heading",
          "Editing entry details"
        ),
        main: t(
          "events:entryAction.editAdditionalDetails.failed.mainMessage.text",
          "Action failed: the changes cannot be saved"
        ),
        additional: t(
          "events:entryAction.editAdditionalDetails.failed.additionalMessage.text",
          "Please contact technical support, if you need help on this matter"
        ),
        okBtn: t(
          "events:entryAction.editAdditionalDetails.failed.ok.button",
          "OK"
        )
      };
    default:
      return null;
  }
});

const { onResult, loading: initFormLoading } = useQuery(
  gql`
    query initEntryUpdateDetailsForm($entryId: ID) {
      initEntryUpdateDetailsForm(entryId: $entryId) {
        vuelidateValidations
        readOnlyFields
      }
    }
  `,
  () => ({
    entryId: entry.value.id
  }),
  () => ({
    enabled: dialogValue.value == true && !entryLoading.value
  })
);

onResult(({ data, loading }) => {
  if (loading) return;
  initEntryUpdateDetailsForm.value = data.initEntryUpdateDetailsForm;
});

async function openFeiActionForm({ entry: e }) {
  entryLoading.value = true;
  dialogValue.value = true;

  try {
    const { data } = await apolloClient.query({
      query: gql`
        query EntryFeiEntryEditFetch($id: ID!) {
          entry(id: $id) {
            id
            connectedWithFei
              billingContact {
                id
                contactType
              }
              canBeApprovedUnderHomeQuota
              allowedMutations
            registration {
              id
              vaultingLungerPersonId
              vaultingReserveLungerPersonId
              registrationGrooms {
                id
                personId
              }
              eventId
              event {
                id
                ${AppShowEventDetails.fragments.event}
              }
              athletes {
                id
                name
                ${AppEntryRegistrationOverview.fragments.personFields}
              }
              horses {
                id
                name
                ${AppEntryRegistrationOverview.fragments.horseFields}
              }
            }
            competition {
              id
              ${AppEntryRegistrationOverview.fragments.competitionFields}
            }
          }
        }
      `,
      variables: {
        id: e.id
      },
      fetchPolicy: "network-only"
    });
    entry.value = data.entry;

    form.value = {
      entryId: data.entry.id,
      vaultingLungerPersonId: data.entry.registration.vaultingLungerPersonId,
      vaultingReserveLungerPersonId:
        data.entry.registration.vaultingReserveLungerPersonId,
      groomPersonId: data.entry.registration.registrationGrooms[0]?.personId
    };
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    entryLoading.value = false;
  }
}

async function submit() {
  formError.value = null;
  if (!formValidate()) {
    formError.value = formErrorMessages();
    return;
  }

  formSubmitted.value = true;

  try {
    const { data, errors } = await apolloClient.mutate({
      mutation: gql`
            mutation entryUpdateDetails(
              $entryId: ID!
              $vaultingLungerPersonId: ID
              $vaultingReserveLungerPersonId: ID
              $groomPersonId: ID
            ) {
              entryUpdateDetails(
                input: {
                  entryId: $entryId
                  vaultingLungerPersonId: $vaultingLungerPersonId
                  vaultingReserveLungerPersonId: $vaultingReserveLungerPersonId
                  groomPersonId: $groomPersonId
                }
              ) {
                feedbackMessages {
                  ${DisplayTextFragments.translation}
                }
                entry {
                  id
                  status
                  ${mutationFragment}
                }
              }
            }
          `,
      variables: {
        entryId: entry.value.id,
        vaultingLungerPersonId: form.value.vaultingLungerPersonId,
        vaultingReserveLungerPersonId: form.value.vaultingReserveLungerPersonId,
        groomPersonId: form.value.groomPersonId
      }
    });

    if (data?.entryUpdateDetails) {
      responseType.value = "success";
    } else if (errors && errors.length > 0) {
      responseType.value = "error";
      mutationErrors.value = errors;
    }
  } catch (error) {
    responseType.value = "error";
    if (error?.graphQLErrors) {
      mutationErrors.value = error.graphQLErrors;
    }
  } finally {
    formSubmitted.value = false;
  }
}

defineExpose({ openFeiActionForm });
</script>
